import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  ClipboardCopy,
  Content,
  List,
  ListComponent,
  ListItem,
  OrderType,
  Spinner,
} from '@patternfly/react-core';
import { DownloadIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAgentDownloads } from '../../../hooks/useAgentDownloads';
import { CopyableCodeBlock } from './CopyableCodeBlock';

interface DeployInstructionsWindowsProps {
  envFileContent: string;
  hasCredentials: boolean;
}

const WINDOWS_AGENT_FILENAME = 'manager-agent.exe';
const WINDOWS_DOWNLOAD_ROUTE = '/organization/agent/download/windows';

/**
 * The Windows guest agent deployment instructions, including the authenticated download of
 * `manager-agent.exe`. The binary route requires the Bearer token, so a plain `<a href>` cannot
 * work: the file is fetched via `useApiFetch` and saved through a blob + temporary object URL.
 * Success streams `application/octet-stream`; failures are JSend `error` at HTTP 200 with
 * `application/json`, so the Content-Type is the only discriminator (same contract as the
 * invoice PDF). Version and sha256 come from the metadata endpoint — the download response's
 * `X-Agent-Version` / `X-Checksum-Sha256` headers are not CORS-exposed and must not be read.
 */
export const DeployInstructionsWindows: React.FC<DeployInstructionsWindowsProps> = ({
  envFileContent,
  hasCredentials,
}) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { downloads, loading: metadataLoading } = useAgentDownloads();
  const [downloading, setDownloading] = useState(false);

  const windows = downloads?.windows ?? null;
  const downloadAvailable = windows?.available === true;

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const response = await apiFetch(windows?.url ?? WINDOWS_DOWNLOAD_ROUTE);
      const contentType = response.headers.get('Content-Type') ?? '';

      // Success and failure are BOTH HTTP 200 — the Content-Type is the only discriminator.
      if (contentType.includes('application/octet-stream')) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = windows?.filename ?? WINDOWS_AGENT_FILENAME;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return;
      }

      // Otherwise it's a JSend error (application/json) at HTTP 200 — surface its message.
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      addNotification({
        title: 'Could not download the Windows agent',
        srTitle: 'Error alert',
        description: body?.message ?? 'Could not download the Windows agent. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      addNotification({
        title: 'Could not download the Windows agent',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected network error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setDownloading(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen, windows]);

  const renderDownloadArea = () => {
    if (metadataLoading) {
      return <Spinner size="md" aria-label="Loading agent download information" />;
    }
    if (!downloadAvailable || !windows) {
      return (
        <Alert variant="info" isInline title="The Windows agent download is not available on this deployment yet." />
      );
    }
    return (
      <>
        <Button
          variant="primary"
          icon={<DownloadIcon />}
          onClick={() => void handleDownload()}
          isLoading={downloading}
          isDisabled={downloading}
        >
          Download {windows.filename ?? WINDOWS_AGENT_FILENAME}
          {windows.version ? ` (v${windows.version})` : ''}
        </Button>
        <Content component="p" className="pf-v6-u-mt-sm pf-v6-u-mb-sm">
          SHA-256:{' '}
          <ClipboardCopy
            variant="inline-compact"
            isCode
            hoverTip="Copy"
            clickTip="Copied"
            copyAriaLabel="Copy SHA-256 checksum"
          >
            {windows.sha256 ?? ''}
          </ClipboardCopy>
          {typeof windows.size_bytes === 'number' ? ` — ${(windows.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
        </Content>
        <CopyableCodeBlock
          id="agent-win-verify-copy"
          code={'Get-FileHash .\\manager-agent.exe -Algorithm SHA256'}
          copyLabel="Copy verify command"
        />
        <Content component="small">
          The binary is not yet Authenticode-signed, so SmartScreen may warn on first run — verify the hash above
          matches before continuing.
        </Content>
      </>
    );
  };

  return (
    <>
      <Content component="p">
        Run these steps as Administrator on each Windows Server that should report to Ancalagon Manager. The Windows
        agent is read-only: it detects Microsoft-licensed software (Windows Server, SQL Server, Remote Desktop
        Services) for SPLA reporting, and never modifies the host.
      </Content>
      <List component={ListComponent.ol} type={OrderType.number}>
        <ListItem className="pf-v6-u-mb-md">
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Download the agent.</strong>
          </Content>
          {renderDownloadArea()}
        </ListItem>
        <ListItem className="pf-v6-u-mb-md">
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Place the binary somewhere stable.</strong> Copy it to a permanent location — the scheduled task
            records this path:
          </Content>
          <CopyableCodeBlock
            id="agent-win-path-copy"
            code={'C:\\Program Files\\Ancalagon\\manager-agent\\manager-agent.exe'}
            copyLabel="Copy path"
          />
        </ListItem>
        <ListItem className="pf-v6-u-mb-md">
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Configure the agent.</strong> From an <strong>elevated</strong> (Administrator) prompt:
          </Content>
          <CopyableCodeBlock id="agent-win-setup-copy" code="manager-agent.exe setup" copyLabel="Copy setup command" />
          <Content component="small">
            The wizard asks for the Keycloak URL, realm, client ID, client secret and Manager URL — the same values as
            the configuration shown in the RHEL based tab. The agent&apos;s config file (
            <code>C:\ProgramData\Ancalagon\manager-agent\agent.env</code>) uses the identical keys. The values to
            enter:
          </Content>
          <CopyableCodeBlock id="agent-win-env-copy" code={envFileContent} copyLabel="Copy configuration" />
          {!hasCredentials && (
            <Content component="small">
              Replace <code>&lt;your-client-secret&gt;</code> with the client secret you saved when the agent was
              enabled.
            </Content>
          )}
        </ListItem>
        <ListItem className="pf-v6-u-mb-md">
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Test the agent.</strong> Do a dry run first (prints the report, sends nothing, needs no
            credentials), then send a first real report:
          </Content>
          <CopyableCodeBlock
            id="agent-win-test-copy"
            code={'manager-agent.exe report --dry-run\nmanager-agent.exe report -v'}
            copyLabel="Copy test commands"
          />
        </ListItem>
        <ListItem>
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Enable hourly reporting.</strong> Register the scheduled task (runs hourly as SYSTEM) and check
            it:
          </Content>
          <CopyableCodeBlock
            id="agent-win-auto-copy"
            code={'manager-agent.exe setup --auto\nschtasks /Query /TN "Ancalagon\\Manager Agent"'}
            copyLabel="Copy scheduled task commands"
          />
        </ListItem>
      </List>
    </>
  );
};
